import { Box, Typography } from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";
import tendinite from "../assets/newImg/tendinites.png";
import SEO, { SITE_URL } from "../components/SEO";
import { SX } from "../sx";
import { C, F, R, S, maxW, trans } from "../tokens";

const TYPES = [
  {
    tag: "Genou",
    title: "Tendinite rotulienne",
    desc: "Courante chez les coureurs et les pratiquants de sports à sauts. Douleur sous la rotule aggravée à la descente des escaliers.",
  },
  {
    tag: "Coude",
    title: "Épicondylite (tennis elbow)",
    desc: "Inflammation de l'insertion des extenseurs du poignet. Douleur à la face externe du coude irradiant dans l'avant-bras.",
  },
  {
    tag: "Talon",
    title: "Tendinite d'Achille",
    desc: "Inflammation du tendon d'Achille, fréquente chez les coureurs. Douleur au démarrage, diminuant à l'échauffement.",
  },
  {
    tag: "Épaule",
    title: "Tendinite de la coiffe",
    desc: "Atteinte des tendons de l'épaule (sus-épineux principalement). Douleur à l'élévation du bras, perturbant le sommeil.",
  },
  {
    tag: "Cuisse",
    title: "Syndrome de l'essuie-glace",
    desc: "Frottement de la bandelette ilio-tibiale sur le condyle fémoral. Douleur latérale de genou chez le coureur de fond.",
  },
  {
    tag: "Hanche",
    title: "Tendinite des ischio-jambiers",
    desc: "Douleur à la partie inférieure de la fesse, aggravée en position assise prolongée et lors des accélérations sportives.",
  },
];

const APPROCHE = [
  {
    num: "01",
    title: "Identifier la cause biomécanique",
    desc: "Une tendinite est rarement isolée : mauvaise foulée, déséquilibre musculaire, surcharge de travail ou compensation posturale. L'ostéopathie traite la cause, pas seulement le tendon.",
  },
  {
    num: "02",
    title: "Réduire les tensions péri-articulaires",
    desc: "Muscles, fascias et capsule articulaire environnants participent à la compression du tendon. Leur relâchement diminue l'inflammation et accélère la guérison.",
  },
  {
    num: "03",
    title: "Restaurer la mécanique globale",
    desc: "Un pied qui compense, un bassin désaxé ou une colonne cervicale tendue modifie toute la chaîne cinématique. L'ostéopathie remet le corps en harmonie.",
  },
  {
    num: "04",
    title: "Prévenir la récidive",
    desc: "Conseils d'échauffement, exercices excentriques, analyse de la charge sportive : la prise en charge ne s'arrête pas à la sortie du cabinet.",
  },
];

const STEPS = [
  {
    num: "01",
    title: "Bilan sportif & gestuel",
    desc: "Type de sport, volume d'entraînement, équipement, antécédents de blessures  pour comprendre le contexte de survenue.",
  },
  {
    num: "02",
    title: "Analyse posturale et articulaire",
    desc: "Évaluation de la mobilité de la chaîne concernée, tests musculaires et palpatoire du tendon et des structures adjacentes.",
  },
  {
    num: "03",
    title: "Traitement ostéopathique",
    desc: "Techniques sur les muscles agonistes/antagonistes, mobilisation des articulations en amont et en aval, travail fascial sur le tendon.",
  },
  {
    num: "04",
    title: "Plan de retour au sport",
    desc: "Progression encadrée pour reprendre l'activité sans récidiver  souvent complémentaire à un suivi kinésithérapeutique.",
  },
];

const Tendinite = () => (
  <>
    <SEO
      title="Tendinites & sport  Ostéopathie à Balma"
      description="Tendinite rotulienne, épicondylite, tendinite d'Achille, coiffe des rotateurs… Maxime Nicolaieff, ostéopathe D.O. à Balma, traite les tendinites et accompagne le retour au sport."
      path="/pathologies/tendinite"
      schema={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Accueil",
            item: `${SITE_URL}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Pathologies",
            item: `${SITE_URL}/pathologies`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Tendinites",
            item: `${SITE_URL}/pathologies/tendinite`,
          },
        ],
      }}
    />

    {/* ── Hero ──────────────────────────────── */}
    <Box component="section" sx={SX.pageHeroSection}>
      <Box
        sx={{
          ...SX.pageHeroInner,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 340px" },
          gap: { xs: "2rem", md: "4rem" },
          alignItems: "center",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabel}>
            Sport & tendinites
          </Box>
          <Typography variant="h1" component="h1" sx={SX.displayTitle}>
            Tendinites <em>reprendre le sport sans rechuter.</em>
          </Typography>
          <Typography sx={SX.pageHeroSub}>
            Une tendinite persistante est le signe d'un déséquilibre mécanique
            plus global. L'ostéopathie ne traite pas seulement le tendon
            douloureux elle corrige la cause pour éviter la récidive.
          </Typography>
          <Box
            sx={{ display: "flex", gap: "1rem", flexWrap: "wrap", mt: "2rem" }}
          >
            <Box
              component="a"
              href="https://www.doctolib.fr/osteopathe/balma/maxime-nicolaieff"
              target="_blank"
              rel="noreferrer"
              sx={SX.btnDark}
            >
              Prendre rendez-vous
            </Box>
            <Box component={Link} to="/pathologies/sportifs" sx={SX.btnOutline}>
              Page Sportifs
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            width: "100%",
            height: { xs: "220px", md: "320px" },
            borderRadius: R.lg,
            overflow: "hidden",
            boxShadow: S.md,
            display: { xs: "none", md: "block" },
          }}
        >
          <Box
            component="img"
            src={tendinite}
            alt="Traitement des tendinites en ostéopathie sportive"
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
      </Box>
    </Box>

    {/* ── Types de tendinites ───────────────── */}
    <Box component="section" sx={{ py: { xs: "3rem", md: "5rem" } }}>
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box component="span" sx={SX.sectionLabel}>
          Localisation
        </Box>
        <Typography
          variant="h2"
          component="h2"
          sx={{ ...SX.displayTitle, mb: "2.5rem" }}
        >
          Les tendinites <em>les plus fréquentes.</em>
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
            },
            gap: "1.25rem",
          }}
        >
          {TYPES.map((t) => (
            <Box
              key={t.title}
              sx={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: R.lg,
                padding: "1.5rem",
                transition: `box-shadow ${trans}`,
                "&:hover": { boxShadow: S.sm },
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: ".62rem",
                  fontWeight: 600,
                  letterSpacing: "2px",
                  textTransform: "uppercase",
                  color: C.accent,
                  display: "block",
                  mb: ".4rem",
                }}
              >
                {t.tag}
              </Box>
              <Typography
                sx={{
                  fontSize: ".9rem",
                  fontWeight: 600,
                  color: C.text,
                  mb: ".5rem",
                }}
              >
                {t.title}
              </Typography>
              <Typography
                sx={{ fontSize: ".8rem", color: C.textMuted, lineHeight: 1.65 }}
              >
                {t.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── Approche ostéopathique ────────────── */}
    <Box
      component="section"
      sx={{ py: { xs: "3rem", md: "5.5rem" }, background: C.bgAlt }}
    >
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box component="span" sx={SX.sectionLabel}>
          Approche
        </Box>
        <Typography
          variant="h2"
          component="h2"
          sx={{ ...SX.displayTitle, mb: "2.5rem" }}
        >
          Traiter la cause, <em>pas seulement le symptôme.</em>
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: "1.5rem",
          }}
        >
          {APPROCHE.map((a) => (
            <Box
              key={a.num}
              sx={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: R.lg,
                padding: "1.75rem",
                display: "grid",
                gridTemplateColumns: "40px 1fr",
                gap: "1rem",
                alignItems: "start",
                transition: `box-shadow ${trans}`,
                "&:hover": { boxShadow: S.sm },
              }}
            >
              <Typography
                sx={{
                  fontFamily: F.display,
                  fontSize: "1.6rem",
                  fontWeight: 300,
                  color: C.accent,
                  lineHeight: 1,
                }}
              >
                {a.num}
              </Typography>
              <Box>
                <Typography
                  sx={{
                    fontSize: ".92rem",
                    fontWeight: 600,
                    color: C.text,
                    mb: ".4rem",
                  }}
                >
                  {a.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: ".82rem",
                    color: C.textMuted,
                    lineHeight: 1.65,
                  }}
                >
                  {a.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── Déroulement ───────────────────────── */}
    <Box component="section" sx={{ py: { xs: "3rem", md: "5.5rem" } }}>
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
          gap: { xs: "3rem", md: "5rem" },
          alignItems: "start",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabel}>
            Déroulement
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{ ...SX.displayTitle, mb: "1rem" }}
          >
            La séance <em>du sportif blessé.</em>
          </Typography>
          <Typography
            sx={{ fontSize: ".85rem", color: C.textMuted, lineHeight: 1.75 }}
          >
            Idéalement complémentaire à un suivi en kinésithérapie pour les
            tendinites chroniques. L'ostéopathe et le kiné travaillent
            différentes dimensions de la même blessure.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {STEPS.map((s, i) => (
            <Box
              key={s.num}
              sx={{
                display: "grid",
                gridTemplateColumns: "48px 1fr",
                gap: "1.25rem",
                alignItems: "start",
                py: "1.5rem",
                borderBottom:
                  i < STEPS.length - 1 ? `1px solid ${C.border}` : "none",
              }}
            >
              <Typography
                sx={{
                  fontFamily: F.display,
                  fontSize: "1.8rem",
                  fontWeight: 300,
                  color: C.accent,
                  lineHeight: 1,
                  pt: ".1rem",
                }}
              >
                {s.num}
              </Typography>
              <Box>
                <Typography
                  sx={{
                    fontSize: ".92rem",
                    fontWeight: 600,
                    color: C.text,
                    mb: ".3rem",
                  }}
                >
                  {s.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: ".82rem",
                    color: C.textMuted,
                    lineHeight: 1.7,
                  }}
                >
                  {s.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── CTA ───────────────────────────────── */}
    <Box
      component="section"
      sx={{ background: C.dark, py: { xs: "3.5rem", md: "6rem" } }}
    >
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
          gap: "3rem",
          alignItems: "center",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabelWhite}>
            Prendre rendez-vous
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontFamily: F.display,
              fontSize: "clamp(2rem, 3.5vw, 3rem)",
              fontWeight: 300,
              color: "#fff",
              lineHeight: 1.2,
              mb: ".75rem",
              "& em": { fontStyle: "italic", color: "rgba(255,255,255,.6)" },
            }}
          >
            Tendinite qui traîne ?<br />
            <em>On règle ça.</em>
          </Typography>
          <Typography
            sx={{
              fontSize: ".85rem",
              color: "rgba(255,255,255,.55)",
              maxWidth: "420px",
              lineHeight: 1.75,
              mb: "1.5rem",
            }}
          >
            Une tendinite non traitée peut devenir chronique en quelques
            semaines. Plus tôt la cause mécanique est corrigée, plus vite vous
            retournez au sport.
          </Typography>
          <Box sx={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <Box
              component="a"
              href="https://www.doctolib.fr/osteopathe/balma/maxime-nicolaieff"
              target="_blank"
              rel="noreferrer"
              sx={SX.btnAccent}
            >
              Réserver une séance
            </Box>
            <Box component="a" href="tel:0695788983" sx={SX.btnOutlineWhite}>
              06 95 78 89 83
            </Box>
          </Box>
        </Box>
        <Box sx={{ textAlign: { xs: "left", md: "center" } }}>
          <Typography
            sx={{
              fontFamily: F.display,
              fontSize: "4.5rem",
              fontWeight: 300,
              color: "#fff",
              lineHeight: 1,
            }}
          >
            1–3
          </Typography>
          <Typography
            sx={{
              fontSize: ".72rem",
              color: "rgba(255,255,255,.4)",
              mt: ".6rem",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            séances
            <br />
            en moyenne
          </Typography>
        </Box>
      </Box>
    </Box>

    {/* ── Liens internes ────────────────────── */}
    <Box
      sx={{
        maxWidth: maxW,
        mx: "auto",
        px: { xs: "1rem", md: "2.5rem" },
        py: { xs: "2.5rem", md: "3rem" },
        display: "flex",
        gap: "1rem",
        flexWrap: "wrap",
      }}
    >
      <Box
        component={Link}
        to="/pathologies/sciatique"
        sx={{
          fontSize: ".85rem",
          color: C.primary,
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Sciatique →
      </Box>
      <Box
        component={Link}
        to="/pathologies/hernie-discale"
        sx={{
          fontSize: ".85rem",
          color: C.primary,
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Hernie discale →
      </Box>
      <Box
        component={Link}
        to="/pathologies/sportifs"
        sx={{
          fontSize: ".85rem",
          color: C.primary,
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Sportifs →
      </Box>
    </Box>
  </>
);

export default Tendinite;
